import React, { FC, ReactElement } from 'react'
import styled, { css } from 'styled-components'
import { rgba } from 'polished'

import { withBox } from 'components/Box'
import Input from 'components/Input'

import FormLabel from '../FormLabel'
import FormError from '../FormError'

// Styles
const HelpTextContainer = styled.div`
  margin-top: 4px;
  text-align: left;
`

const HelpText = styled.small`
  display: block;
  color: ${({ theme }) => theme.colors.text.dark.tertiary};
`

const ErrorList = styled.ul`
  padding: 0;
  margin: 0;
  margin-top: 4px;
  text-align: left;

  li {
    padding: 0;
    margin: 0;
    list-style: none;
  }
`

// Types
interface StripeFormFieldProps {
  className?: string
  id?: string
  name?: string
  label?: string
  errorText?: string | string[]
  children?: ReactElement
  help?: string
  htmlFor: string
}

// Helper functions
const renderErrorTextArray = (errorTextArray: string[]) => (
  <ErrorList>
    {errorTextArray.map((errorText, i) => (
      <li key={i}>
        <FormError>{errorText}</FormError>
      </li>
    ))}
  </ErrorList>
)

const renderErrorText = (errorText: string) =>
  errorText ? renderErrorTextArray([errorText]) : null

// Unstyled StripeFormField
const StripeFormField: FC<StripeFormFieldProps> = ({
  label,
  errorText,
  help,
  htmlFor,
  children,
  ...props
}) => {
  return (
    <div {...props}>
      {label && <FormLabel htmlFor={htmlFor}>{label}</FormLabel>}
      {children}
      {errorText &&
        (Array.isArray(errorText)
          ? renderErrorTextArray(errorText)
          : renderErrorText(errorText))}
      {help && (
        <HelpTextContainer>
          <HelpText>{help}</HelpText>
        </HelpTextContainer>
      )}
    </div>
  )
}

const StyledStripeFormField = styled(withBox(StripeFormField))`
  position: relative;
  margin-bottom: 1rem; /* stylelint-disable-line scales/space */

  /*
  Force the input border to be red if there is an external error.

  E.g. stripe.createSource errors don't affect the inputs, or set
  any flag in them this way we can maintain consistency in the border.
  */
  ${Input} {
    ${({ errorText, theme }) =>
      errorText &&
      css`
        border-color: ${rgba(theme.colors.negative, 0.5)};
      `};
  }
  &:last-child {
    margin-bottom: 0;
  }
`

export default StyledStripeFormField
